import React, { useEffect, useState } from 'react';
import { getApiBase } from '../../utils/apiConfig';

// Regulatory Events · Global Reference — owner audit 2026-09-26.
//  · SR-352 the process stage is shown as a stage, never as a probability.
//  · SR-353 an exact date only when date_state = VERIFIED (captured primary document); EXPECTED shows the
//    month. No client-side fallback list and no price-reaction claims.

export type RegStage = 'in_force_date' | 'final_published' | 'consultation' | 'proposed' | 'anticipated';

export const STAGE_LABEL: Record<RegStage, string> = {
  in_force_date: 'Effective date set',
  final_published: 'Final text published',
  consultation: 'In consultation',
  proposed: 'Proposed (draft)',
  anticipated: 'Anticipated',
};

interface RegEvent {
  id: string;
  date: string;
  date_state: 'VERIFIED' | 'EXPECTED';
  title: string;
  type: string;
  jurisdiction: string;
  impact_level: string;
  impact_basis?: string;
  stage?: RegStage;
  source?: string;
  sourceUrl?: string;
  primarySourceDocument?: string;
  last_verified?: string;
}

function dateLabel(e: RegEvent): { main: string; sub: string } {
  if (e.date_state === 'VERIFIED' && /^\d{4}-\d{2}-\d{2}$/.test(e.date)) {
    const [y, m, d] = e.date.split('-').map(Number);
    return { main: `${d} ${MONTHS[m - 1]}`, sub: `${y} · verified date` };
  }
  const [y, m] = e.date.split('-').map(Number);
  return { main: `${MONTHS[m - 1]} ${y}`, sub: 'expected · date not primary-verified' };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const IMPACT_TEXT: Record<string, string> = { high: 'text-[color:var(--c-hex-d08a8a)]', medium: 'text-[color:var(--c-amber)]', low: 'text-[color:var(--c-text-strong)]' };

const RegulatoryEvents: React.FC<{ months?: number }> = ({ months = 12 }) => {
  const [events, setEvents] = useState<RegEvent[]>([]);
  const [tracked, setTracked] = useState(0);
  const [withheld, setWithheld] = useState(0);
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(false); // collapsed by default (owner 2026-09-26)

  useEffect(() => {
    fetch(`${getApiBase()}/api/regulatory/events?months=${months}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d?.success || !Array.isArray(d.data)) {
          setState('unavailable');
          return;
        }
        const today = new Date().toISOString().slice(0, 10);
        const upcoming = (d.data as RegEvent[])
          .filter((e) => (e.date_state === 'VERIFIED' ? e.date >= today : e.date >= today.slice(0, 7)))
          .sort((a, b) => a.date.localeCompare(b.date));
        setEvents(upcoming);
        setTracked(typeof d.tracked === 'number' ? d.tracked : d.data.length);
        setWithheld(typeof d.withheld_unverified === 'number' ? d.withheld_unverified : 0);
        setState('ready');
      })
      .catch(() => setState('unavailable'));
  }, [months]);

  return (
    <div className="rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.08)] bg-[color:var(--c-panel-deep)] p-5">
      <div className={`flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between ${open ? 'mb-4' : ''}`}>
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="flex items-center gap-2 text-left focus:outline-none focus-visible:underline">
          <span className="font-mono text-[12px] text-[color:var(--c-text-soft)]">{open ? '▾' : '▸'}</span>
          <h3 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[color:var(--c-text)]">Regulatory events · global reference</h3>
        </button>
        <div
          className="font-mono text-[12px] text-[color:var(--c-text-body)]"
          title="Exact dates are shown only when a captured primary document states them (VERIFIED). EXPECTED items show the month. The process stage describes where a rule is in its lifecycle — it is not a probability. Impact class is a qualitative scope label. Context only; no event affects any score."
        >
          {state === 'ready' ? `${events.length} upcoming · ${tracked} tracked · ${withheld} withheld pending primary-source verification · context only ⓘ` : ''}
        </div>
      </div>

      {open && state === 'loading' && <p className="py-6 text-center text-sm text-[color:var(--c-text-soft)]">Loading events…</p>}
      {open && state === 'unavailable' && <p className="py-6 text-center text-sm text-[color:var(--c-text-body)]">Regulatory events are unavailable. No client-side substitute is shown.</p>}
      {open && state === 'ready' && events.length === 0 && (
        <p className="py-6 text-center text-[13px] text-[color:var(--c-text-body)]">
          No primary-source-verified regulatory events in the next {months} months.{withheld > 0 ? ` ${withheld} tracked item(s) are withheld until their dates are verified from a primary document.` : ''}
        </p>
      )}

      {open && state === 'ready' && events.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {(showAll ? events : events.slice(0, 6)).map((e) => {
            const dl = dateLabel(e);
            const doc = e.primarySourceDocument || e.sourceUrl;
            return (
              <article key={e.id} className="flex flex-col rounded border border-[color:rgb(var(--c-ink-rgb)/0.07)] bg-[color:var(--c-panel-black)] px-4 py-3">
                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <div>
                    <div className="font-mono text-[15px] font-semibold text-[color:var(--c-gold)]">{dl.main}</div>
                    <div className={`font-mono text-[11px] ${e.date_state === 'VERIFIED' ? 'text-[color:var(--c-text-soft)]' : 'text-[color:var(--c-gold-light)]'}`}>{dl.sub}</div>
                  </div>
                  <span className="font-mono text-[12px] text-[color:var(--c-text-body)]">{e.jurisdiction}</span>
                </div>
                <p className="mb-2 text-[13.5px] leading-snug text-[color:var(--c-gray-100)]">{e.title}</p>
                <div className="mt-auto flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-[color:var(--c-text-body)]">
                  {e.stage && <span>Stage: {STAGE_LABEL[e.stage] ?? e.stage}</span>}
                  <span className={IMPACT_TEXT[e.impact_level?.toLowerCase()] ?? 'text-[color:var(--c-text-strong)]'} title={e.impact_basis || 'Qualitative scope class, not a forecast.'}>
                    Impact class: {e.impact_level}
                  </span>
                  {doc && (
                    <a href={doc} target="_blank" rel="noopener noreferrer" className="text-[color:var(--c-gold)] hover:underline">
                      {e.source || 'Primary source'} ↗
                    </a>
                  )}
                  {e.last_verified && <span className="font-mono text-[11px] text-[color:var(--c-text-soft)]">reviewed {e.last_verified}</span>}
                </div>
              </article>
            );
          })}
        </div>
      )}
      {open && state === 'ready' && events.length > 6 && (
        <button type="button" onClick={() => setShowAll(!showAll)} className="mt-3 text-[12px] text-[color:var(--c-gold)] hover:underline focus:outline-none focus-visible:underline">
          {showAll ? 'Show fewer' : `Show all ${events.length} events`}
        </button>
      )}
    </div>
  );
};

export default RegulatoryEvents;
